import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import './MonitoringDashboard.css';

interface MonitoringDashboardProps {
  apiBaseUrl?: string;
}

interface ServiceStats {
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  successRate: number;
  averageDuration: number;
  p95Duration: number;
  p99Duration: number;
}

interface ServiceMetrics {
  serviceName: string;
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  successRate: string;
  averageDuration: string;
  p95: string;
  p99: string;
  errorCodes: Array<{ code: string; count: number }>;
  methods: Array<{ method: string; count: number }>;
}

interface RecentCall {
  timestamp: number;
  service: string;
  method: string;
  duration: number;
  success: boolean;
  errorCode?: string;
}

interface AlertItem {
  level: 'critical' | 'warning' | 'info';
  service: string;
  message: string;
}

export const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({ 
  apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001' 
}) => {
  // Authentication state
  const [password, setPassword] = useState<string>('');
  const [token, setToken] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  // Data state
  const [stats, setStats] = useState<ServiceStats | null>(null);
  const [services, setServices] = useState<ServiceMetrics[]>([]);
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);

  // UI state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'services' | 'recent'>('services');

  // Check for stored token on mount
  useEffect(() => {
    const storedToken = sessionStorage.getItem('monitorToken');
    if (storedToken) {
      setToken(storedToken);
    }
  }, []);

  // Authentication
  const handleLogin = async (): Promise<void> => {
    if (!password.trim()) {
      setAuthError('Please enter a password');
      return;
    }

    setAuthError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${apiBaseUrl}/api/monitor/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (response.ok && data.token) {
        setToken(data.token);
        sessionStorage.setItem('monitorToken', data.token);
        setPassword('');
      } else {
        setAuthError(data.error || 'Authentication failed');
      }
    } catch (err) {
      setAuthError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = (): void => {
    setToken(null);
    sessionStorage.removeItem('monitorToken');
    setStats(null);
    setServices([]);
    setRecentCalls([]);
    setAlerts([]);
  };

  // Data fetching
  const fetchStats = useCallback(async (): Promise<void> => {
    if (!token) return;

    try {
      const response = await fetch(`${apiBaseUrl}/api/monitor/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setStats(data.stats);
      setLastUpdate(new Date().toLocaleTimeString());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    }
  }, [apiBaseUrl, token]);

  const fetchServices = useCallback(async (): Promise<void> => {
    if (!token) return;

    try {
      const response = await fetch(`${apiBaseUrl}/api/monitor/services`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setServices(data.services || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch services');
    }
  }, [apiBaseUrl, token]);

  const fetchRecentCalls = useCallback(async (): Promise<void> => {
    if (!token) return;

    try {
      const response = await fetch(`${apiBaseUrl}/api/monitor/recent-calls`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setRecentCalls(data.recentCalls || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch recent calls');
    }
  }, [apiBaseUrl, token]);

  const fetchAlerts = useCallback(async (): Promise<void> => {
    if (!token) return;

    try {
      const response = await fetch(`${apiBaseUrl}/api/monitor/alerts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        handleLogout();
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setAlerts(data.alerts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch alerts');
    }
  }, [apiBaseUrl, token]);

  const fetchAllData = useCallback(async (): Promise<void> => {
    if (!token) return;
    
    setIsLoading(true);
    await Promise.all([
      fetchStats(),
      fetchServices(),
      fetchRecentCalls(),
      fetchAlerts(),
    ]);
    setIsLoading(false);
  }, [token, fetchStats, fetchServices, fetchRecentCalls, fetchAlerts]);

  const handleReset = useCallback(async (): Promise<void> => {
    if (!token) return;
    if (!window.confirm('Are you sure you want to reset all monitoring statistics?')) {
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/monitor/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchAllData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset statistics');
    }
  }, [apiBaseUrl, token, fetchAllData]);

  // Initial data fetch and auto-refresh
  useEffect(() => {
    if (token) {
      void fetchAllData();
    }
  }, [token, fetchAllData]);

  useEffect(() => {
    if (!token || !autoRefresh) return;

    const interval = setInterval(() => {
      void fetchAllData();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [token, autoRefresh, fetchAllData]);

  // Render login screen
  if (!token) {
    return (
      <div className="monitoring-login">
        <Card className="monitoring-login-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: '"Sometype Mono", monospace' }}>
              Service Monitoring Dashboard
            </CardTitle>
            <CardDescription>
              Enter password to access monitoring tools
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void handleLogin();
                  }
                }}
              />
              {authError && (
                <Alert variant="destructive">
                  <AlertDescription>{authError}</AlertDescription>
                </Alert>
              )}
              <Button 
                onClick={() => void handleLogin()} 
                disabled={isLoading}
              >
                {isLoading ? 'Authenticating...' : 'Login'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main dashboard
  return (
    <div className="monitoring-container">
      {/* Header */}
      <div className="monitoring-header">
        <div className="monitoring-header-title">
          <h1>🔍 Service Monitoring Dashboard</h1>
          <p>Last updated: {lastUpdate} {autoRefresh && '(Auto-refreshing every 5s)'}</p>
        </div>
        <div className="monitoring-header-controls">
          <label className="monitoring-auto-refresh">
            <input 
              type="checkbox" 
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <Button 
            onClick={() => void fetchAllData()} 
            disabled={isLoading}
            size="sm"
            variant="outline"
          >
            {isLoading ? '...' : 'Refresh'}
          </Button>
          <Button 
            onClick={() => void handleReset()} 
            variant="destructive"
            size="sm"
          >
            Reset
          </Button>
          <Button 
            onClick={handleLogout}
            variant="outline"
            size="sm"
          >
            Logout
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" style={{ marginBottom: '20px' }}>
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Alerts Panel */}
      {alerts.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          {alerts.map((alert, idx) => (
            <div 
              key={idx}
              className={`monitoring-alert ${alert.level}`}
            >
              <div className="monitoring-alert-title">
                <Badge variant={alert.level === 'critical' ? 'destructive' : alert.level === 'warning' ? 'warning' : 'info'}>
                  {alert.level.toUpperCase()}
                </Badge>
                {alert.service}
              </div>
              <div className="monitoring-alert-message">{alert.message}</div>
            </div>
          ))}
        </div>
      )}

      {/* Stats Cards */}
      <div className="monitoring-stats-grid">
        <Card className="monitoring-stat-card">
          <CardHeader style={{ paddingBottom: '8px' }}>
            <CardDescription className="monitoring-stat-label">Total Calls</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="monitoring-stat-value">
              {stats?.totalCalls.toLocaleString() || '0'}
            </div>
          </CardContent>
        </Card>

        <Card className="monitoring-stat-card">
          <CardHeader style={{ paddingBottom: '8px' }}>
            <CardDescription className="monitoring-stat-label">Success Rate</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`monitoring-stat-value ${
              stats && stats.successRate >= 95 ? 'success' : 
              stats && stats.successRate >= 90 ? 'warning' : 'error'
            }`}>
              {stats?.successRate.toFixed(1) || '0.0'}%
            </div>
          </CardContent>
        </Card>

        <Card className="monitoring-stat-card">
          <CardHeader style={{ paddingBottom: '8px' }}>
            <CardDescription className="monitoring-stat-label">Failed Calls</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="monitoring-stat-value error">
              {stats?.failedCalls.toLocaleString() || '0'}
            </div>
          </CardContent>
        </Card>

        <Card className="monitoring-stat-card">
          <CardHeader style={{ paddingBottom: '8px' }}>
            <CardDescription className="monitoring-stat-label">Avg Duration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="monitoring-stat-value">
              {stats?.averageDuration.toFixed(1) || '0.0'}ms
            </div>
          </CardContent>
        </Card>

        <Card className="monitoring-stat-card">
          <CardHeader style={{ paddingBottom: '8px' }}>
            <CardDescription className="monitoring-stat-label">P95 Duration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="monitoring-stat-value">
              {stats?.p95Duration.toFixed(1) || '0.0'}ms
            </div>
          </CardContent>
        </Card>

        <Card className="monitoring-stat-card">
          <CardHeader style={{ paddingBottom: '8px' }}>
            <CardDescription className="monitoring-stat-label">P99 Duration</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="monitoring-stat-value">
              {stats?.p99Duration.toFixed(1) || '0.0'}ms
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="monitoring-tabs">
        <div className="monitoring-tabs-list">
          <button 
            className={`monitoring-tab-trigger ${activeTab === 'services' ? 'active' : ''}`}
            onClick={() => setActiveTab('services')}
          >
            Services
          </button>
          <button 
            className={`monitoring-tab-trigger ${activeTab === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveTab('recent')}
          >
            Recent Calls
          </button>
        </div>

        <div className="monitoring-tab-content active">
          {activeTab === 'services' ? (
            <div>
              <h3 style={{ marginBottom: '16px' }}>Service Breakdown</h3>
              {services.length === 0 ? (
                <div className="monitoring-empty-state">
                  <p>No service data available yet</p>
                  <p style={{ fontSize: '12px' }}>Services will appear here as they are used</p>
                </div>
              ) : (
                <div className="monitoring-scroll-area">
                  <div className="monitoring-service-list">
                    {services.map((service) => (
                      <div key={service.serviceName} className="monitoring-service-item">
                        <div className="monitoring-service-header">
                          <div className="monitoring-service-name">{service.serviceName}</div>
                          <Badge variant={parseFloat(service.successRate) >= 95 ? 'success' : parseFloat(service.successRate) >= 90 ? 'warning' : 'destructive'}>
                            {service.successRate}% success
                          </Badge>
                        </div>
                        
                        <div className="monitoring-service-metrics">
                          <div className="monitoring-metric">
                            <div className="monitoring-metric-label">Total Calls</div>
                            <div className="monitoring-metric-value">{service.totalCalls.toLocaleString()}</div>
                          </div>
                          <div className="monitoring-metric">
                            <div className="monitoring-metric-label">Failed</div>
                            <div className="monitoring-metric-value" style={{ color: '#f85149' }}>{service.failedCalls.toLocaleString()}</div>
                          </div>
                          <div className="monitoring-metric">
                            <div className="monitoring-metric-label">Avg Duration</div>
                            <div className="monitoring-metric-value">{service.averageDuration}ms</div>
                          </div>
                          <div className="monitoring-metric">
                            <div className="monitoring-metric-label">P95</div>
                            <div className="monitoring-metric-value">{service.p95}ms</div>
                          </div>
                        </div>

                        {service.methods.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '6px' }}>Top Methods</div>
                            <div className="monitoring-badges-row">
                              {service.methods.slice(0, 5).map((method) => (
                                <Badge key={method.method} variant="outline" style={{ fontSize: '10px' }}>
                                  {method.method} ({method.count})
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {service.errorCodes.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '6px' }}>Error Codes</div>
                            <div className="monitoring-badges-row">
                              {service.errorCodes.map((error) => (
                                <Badge key={error.code} variant="destructive" style={{ fontSize: '10px' }}>
                                  {error.code} ({error.count})
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h3 style={{ marginBottom: '16px' }}>Recent Service Calls</h3>
              {recentCalls.length === 0 ? (
                <div className="monitoring-empty-state">
                  <p>No recent calls recorded</p>
                  <p style={{ fontSize: '12px' }}>Service calls will appear here as they happen</p>
                </div>
              ) : (
                <div className="monitoring-scroll-area">
                  <div className="monitoring-recent-calls">
                    {recentCalls.map((call, idx) => (
                      <div 
                        key={idx}
                        className={`monitoring-call-item ${call.success ? 'success' : 'failed'}`}
                      >
                        <div className="monitoring-call-info">
                          <Badge variant={call.success ? 'success' : 'destructive'} style={{ fontSize: '9px', minWidth: '60px' }}>
                            {call.success ? 'SUCCESS' : 'FAILED'}
                          </Badge>
                          <span className="monitoring-call-time">
                            {new Date(call.timestamp).toLocaleTimeString()}
                          </span>
                          <span className="monitoring-call-service">{call.service}</span>
                          <span className="monitoring-call-method">{call.method}</span>
                          {call.errorCode && (
                            <Badge variant="outline" style={{ fontSize: '9px' }}>
                              {call.errorCode}
                            </Badge>
                          )}
                        </div>
                        <span className={`monitoring-call-duration ${call.duration > 100 ? 'slow' : 'fast'}`}>
                          {call.duration.toFixed(1)}ms
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
